import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import type { PropertyEditorProps } from "@ext/properties/components/Modals/PropertyEditor";
import { useUpdateCatalogProperty } from "@ext/properties/logic/hooks/useUpdateCatalogProperty";
import type { Property, PropertyValue } from "@ext/properties/models";
import { useCallback, useEffect, useRef } from "react";

interface UsePropertyEditorOptions {
	canAdd: boolean;
	catalogProperties: Map<string, Property>;
	properties: Property[] | PropertyValue[];
}

export const usePropertyEditor = ({ canAdd, catalogProperties, properties }: UsePropertyEditorOptions) => {
	const articleProps = ArticlePropsService.value;
	const isOpenRef = useRef(false);
	const saveCatalogProperties = useUpdateCatalogProperty({ canAdd });

	// biome-ignore lint/correctness/useExhaustiveDependencies: changing articles must close the current editor
	useEffect(() => {
		if (!isOpenRef.current) return;
		ModalToOpenService.resetValue();
		isOpenRef.current = false;
	}, [articleProps.logicPath]);

	return useCallback(
		(id: string | null) => {
			isOpenRef.current = true;
			ModalToOpenService.setValue<PropertyEditorProps>(ModalToOpen.PropertySettings, {
				properties,
				onlyArticlePropertyTypes: true,
				data: id === null ? undefined : catalogProperties.get(id),
				onSubmit: async (property) => {
					await saveCatalogProperties(property);
					ModalToOpenService.resetValue();
				},
				onDelete: async (isArchive: boolean) => {
					if (!id) return;
					const property = catalogProperties.get(id);
					if (!property) return;
					await saveCatalogProperties(property, true, isArchive);
					ModalToOpenService.resetValue();
				},
				onClose: () => {
					isOpenRef.current = false;
					ModalToOpenService.resetValue();
				},
			});
		},
		[catalogProperties, properties, saveCatalogProperties],
	);
};
